// crewbit-admin/src/components/money/OutstandingTableRow.tsx
//
// Single <tr> for the admin Outstanding table. Renders one
// OutstandingInvoiceRow with cells for Invoice / Client / Amount /
// Days Out / Last Reminded / Status / Action.
//
// SPEC: docs/references/MONEY_MODULE_SPEC_v1.md §Outstanding.7.2.
// Decision §Outstanding.D3: Remind button hidden when bucket === 'current'.
// Tone routing: every bucket-derived color goes through bucketTone(row.bucket).

import { useState } from 'react';
import { Send } from 'lucide-react';
import { palette, urgency } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';
import { bucketTone } from '../../lib/aging';
import type { OutstandingInvoiceRow } from '../../hooks/useOutstandingInvoices';

interface OutstandingTableRowProps {
  row: OutstandingInvoiceRow;
  onTap: () => void;
  onRemind: () => void;
}

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13,
  color: palette.g700,
  borderBottom: `1px solid ${palette.g100}`,
  verticalAlign: 'middle',
};

function toneTextColor(tone: 'neutral' | 'warning' | 'danger'): string {
  switch (tone) {
    case 'neutral': return palette.g700;
    case 'warning': return urgency.warning;
    case 'danger': return palette.red;
  }
}

function formatRelativeTime(iso: string, nowMs: number): string {
  const ms = nowMs - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusBadge(status: OutstandingInvoiceRow['status'], daysOutstanding: number) {
  const label = status === 'overdue' ? `${daysOutstanding}d overdue` : status;
  const isOverdue = status === 'overdue';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11.5,
      fontWeight: 500,
      textTransform: status === 'overdue' ? 'none' : 'capitalize',
      background: isOverdue ? palette.redWash : palette.g100,
      color: isOverdue ? palette.red : palette.g700,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

export function OutstandingTableRow({ row, onTap, onRemind }: OutstandingTableRowProps) {
  const [nowMs] = useState(() => Date.now());

  const tone = bucketTone(row.bucket);
  const daysColor = toneTextColor(tone);
  const showRemind = row.bucket !== 'current';

  const remindedAtMs = row.last_reminder_sent_at
    ? new Date(row.last_reminder_sent_at).getTime()
    : null;
  const remindedRecently = remindedAtMs !== null && nowMs - remindedAtMs < 86_400_000;

  const daysLabel = row.days_outstanding === 0 ? 'Today' : `${row.days_outstanding}d`;

  const handleRemindClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemind();
  };

  return (
    <tr
      onClick={onTap}
      style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = palette.g50)}
      onMouseLeave={(e) => (e.currentTarget.style.background = '')}
    >
      <td style={{ ...tdStyle, fontWeight: 600, color: palette.g800 }}>
        {row.invoice_number}
      </td>
      <td style={{ ...tdStyle, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {row.client_name ?? '—'}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: palette.g800 }}>
        {formatMoney(row.total)}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: daysColor, fontWeight: 600 }}>
        {daysLabel}
      </td>
      <td style={{ ...tdStyle, color: palette.g500 }}>
        {row.last_reminder_sent_at ? formatRelativeTime(row.last_reminder_sent_at, nowMs) : '—'}
      </td>
      <td style={tdStyle}>
        {statusBadge(row.status, row.days_outstanding)}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {showRemind ? (
          <button
            onClick={handleRemindClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '5px 10px',
              borderRadius: 6,
              border: `1px solid ${palette.g200}`,
              background: remindedRecently ? palette.g50 : palette.white,
              fontSize: 12,
              fontWeight: 500,
              color: remindedRecently ? palette.g500 : palette.g700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background 150ms ease',
            }}
          >
            <Send size={11} /> Remind
          </button>
        ) : null}
      </td>
    </tr>
  );
}
